import { useState } from 'react';
import { Edit2, Play, X } from 'lucide-react';

interface PromptEditorProps {
  stageName: string;
  stageNumber: number;
  defaultPrompt: string;
  onApprove: (editedPrompt: string) => void;
  onCancel: () => void;
}

export function PromptEditor({
  stageName,
  stageNumber,
  defaultPrompt,
  onApprove,
  onCancel
}: PromptEditorProps) {
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [isEditing, setIsEditing] = useState(false);

  const handleApprove = () => {
    onApprove(prompt);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Stage {stageNumber}: {stageName}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Review and edit the prompt before sending to AI
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Prompt Content */}
        <div className="flex-1 overflow-auto p-4">
          {isEditing ? (
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="w-full h-full min-h-[400px] p-4 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Edit prompt..."
            />
          ) : (
            <pre className="w-full h-full min-h-[400px] p-4 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm whitespace-pre-wrap overflow-auto">
              {prompt}
            </pre>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Edit2 className="h-4 w-4" />
            <span>{isEditing ? 'Preview' : 'Edit Prompt'}</span>
          </button>

          <div className="flex items-center space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel Generation
            </button>
            <button
              onClick={handleApprove}
              className="flex items-center space-x-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Play className="h-4 w-4" />
              <span>Continue with this prompt</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
